/**
 * "Send once, at a set time, to a segment" in words and in the shapes the
 * server reads: the UK time now, a once time as a person reads it, the list
 * row's status, and a segment built on the trigger card as a search body.
 */
import type { LeadFilters } from '../../api/queries';
import type { WorkflowSummary } from '../../api/workflows';
import { formatNumber } from '../../theme';

/** The UK date and time now, `YYYY-MM-DDTHH:MM`, as a once schedule's `at`. */
export function ukNow(d: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/** The UK date a number of days from now, `YYYY-MM-DD`. */
export function ukDate(daysOn: number, d: Date = new Date()): string {
  return ukNow(new Date(d.getTime() + daysOn * 86_400_000)).slice(0, 10);
}

const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-28T10:00" as "Wed 28 Oct, 10:00". The time is UK time already. */
export function onceWords(at: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}:\d{2}))?/.exec(at);
  if (!m) return at;
  const [, y, mo, d, time] = m;
  const day = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d))).getUTCDay();
  const date = `${DAY[day]} ${Number(d)} ${MONTH[Number(mo) - 1]}`;
  return time ? `${date}, ${time}` : date;
}

/** The list row's line for a workflow's "send once", or null. */
export function sendOnceStatus(s: WorkflowSummary['send_once']): { text: string; color: string } | null {
  if (!s) return null;
  switch (s.state) {
    case 'scheduled':
      return {
        text: `Scheduled for ${onceWords(s.at)}${s.waiting_for_check ? ', once its addresses are checked' : ''}`,
        color: s.waiting_for_check ? 'chartAmber' : 'blue',
      };
    case 'sending':
      return { text: `Sending: ${formatNumber(s.done)} of ${formatNumber(s.total)}`, color: 'teal' };
    case 'sent':
      return { text: 'Sent', color: 'gray' };
    default:
      return null;
  }
}

/** A segment built on the trigger card. */
export type BuiltSegment = {
  campaignIds: string[];
  /** Arrived in the last so many days; 0 for any time. */
  days: number;
  fieldFilters: NonNullable<LeadFilters['field_filters']>;
  ignoreCampaigns: boolean;
};

export const BLANK_SEGMENT: BuiltSegment = { campaignIds: [], days: 0, fieldFilters: [], ignoreCampaigns: false };

/** The most days a rolling range takes (`date_range: "Nd"`). */
export const MAX_DAYS = 3650;

/** A built segment as the search body: only what is set, and rules with a field. */
export function builtFilter(s: BuiltSegment): LeadFilters {
  const out: LeadFilters = {};
  if (s.campaignIds.length) out.campaign_ids = s.campaignIds;
  if (s.days > 0) out.date_range = `${Math.min(Math.floor(s.days), MAX_DAYS)}d`;
  const rules = s.fieldFilters.filter((r) => r.field.trim());
  if (rules.length) out.field_filters = rules.map((r) => ({ ...r, field: r.field.trim() }));
  if (s.ignoreCampaigns) out.ignore_campaign_filter = true;
  return out;
}
